import { apiClient } from './client'
import type {
  CreateStepRequest,
  InsertProjectReferenceRequest,
  StepNode,
  UpdateStepRequest,
} from './types'

export const createStep = async (projectId: string, data: CreateStepRequest): Promise<StepNode> => {
  const response = await apiClient.post<StepNode>(`/api/v1/projects/${projectId}/steps`, data)
  return response.data
}

/**
 * Phase 11a: atomic single-step move. Replaces the two-PATCH sortOrder-swap chain
 * `useStepMoves` did before — that left a duplicate `sortOrder` between writes.
 *
 * `newParentId` null = keep current parent (within-parent reorder).
 * `newSortOrder` = destination index in the post-move sibling list (0-based; clamped).
 */
export interface MoveStepRequest {
  stepId: string
  newParentId?: string | null
  newSortOrder: number
}

export const moveStep = async (projectId: string, data: MoveStepRequest): Promise<StepNode> => {
  const response = await apiClient.patch<StepNode>(`/api/v1/projects/${projectId}/tree/move`, data)
  return response.data
}

export const updateStep = async (stepId: string, data: UpdateStepRequest): Promise<StepNode> => {
  const response = await apiClient.patch<StepNode>(`/api/v1/steps/${stepId}`, data)
  return response.data
}

export const deleteStep = async (stepId: string): Promise<void> => {
  await apiClient.delete(`/api/v1/steps/${stepId}`)
}

/**
 * Insert a Snapshot of {@code sourceProjectId} as a new PROJECT_REFERENCE child under {@code
 * stepId}. Server returns the parent's freshly-hydrated subtree.
 */
export const insertProjectReference = async (
  stepId: string,
  data: InsertProjectReferenceRequest
): Promise<StepNode> => {
  const response = await apiClient.post<StepNode>(
    `/api/v1/steps/${stepId}/insert-project-reference`,
    data
  )
  return response.data
}

/**
 * Re-run the snapshot for an existing PROJECT_REFERENCE Step. Returns the reference step's new
 * subtree (the wholesale replacement of its children).
 */
export const refreshFromSource = async (stepId: string): Promise<StepNode> => {
  const response = await apiClient.post<StepNode>(`/api/v1/steps/${stepId}/refresh-from-source`)
  return response.data
}
